import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, GraduationCap, ArrowUpRight } from "lucide-react";
import { scrollToId } from "@/hooks/useLenis";

const LINKS = [
  { id: "about", label: "About Me" },
  { id: "books", label: "Books" },
  { id: "journey", label: "Professional Summary" },
  { id: "contact", label: "Contact Me" },
];

function PlaygroundButton({ className = "", onClick }) {
  const location = useLocation();
  const navigate = useNavigate();

  const goToSection = (e) => {
    onClick?.(e);
    if (location.pathname === "/") {
      scrollToId("playground");
      return;
    }
    navigate("/#playground");
  };

  return (
    <button
      type="button"
      onClick={goToSection}
      data-testid="nav-playground"
      className={`group relative inline-flex items-center justify-center rounded-full p-[1.5px] overflow-hidden ${className}`}
    >
      {/* Rotating blue border highlight */}
      <span
        aria-hidden="true"
        className="absolute inset-[-40%] animate-border-spin bg-[conic-gradient(from_0deg,transparent_25%,#6D84FF_50%,transparent_75%)] opacity-90"
      />
      <span
        aria-hidden="true"
        className="absolute inset-0 rounded-full shadow-[0_0_12px_rgba(109,132,255,0.35)] pointer-events-none"
      />
      <span className="relative z-10 inline-flex w-full items-center justify-center gap-2 rounded-full bg-ink-0/95 px-4 py-2 text-sm text-ink/70 transition-colors group-hover:text-ink">
        Automation Playground
      </span>
    </button>
  );
}

const SECTION_IDS = LINKS.map((l) => l.id);

export default function Navigation() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [active, setActive] = useState(SECTION_IDS[0]);

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 24);

      const trigger = window.scrollY + window.innerHeight * 0.35;
      let current = SECTION_IDS[0];
      for (const id of SECTION_IDS) {
        const el = document.getElementById(id);
        if (el && el.offsetTop <= trigger) current = id;
      }
      setActive(current);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <>
      <motion.header
        initial={{ y: -32, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
        className={`fixed top-0 inset-x-0 z-50 transition-[background,border,backdrop-filter] duration-300 ${
          scrolled
            ? "backdrop-blur-xl bg-ink-0/70 border-b border-ink/10"
            : "bg-transparent border-b border-transparent"
        }`}
        data-testid="site-nav"
      >
        <nav className="section-x mx-auto max-w-[1400px] flex items-center justify-between h-16 md:h-20">
          <button
            onClick={() => handleNav("hero")}
            className="group flex items-center gap-3 focus:outline-none"
            data-testid="nav-logo"
            aria-label="Chirag Khimani — home"
          >
            <span className="relative inline-flex h-8 w-8 items-center justify-center rounded-full border border-ink/15 bg-ink/[0.03]">
              <span className="absolute inset-0 rounded-full bg-gradient-to-br from-accent-cyan/40 to-accent-purple/30 opacity-70 blur-md" />
              <GraduationCap className="relative h-[18px] w-[18px] text-accent-cyan" />
            </span>
            <span className="font-display font-semibold text-[15px] tracking-tight">
              Chirag Khimani
            </span>
          </button>

          <div className="hidden md:flex items-center gap-1">
            {LINKS.map((l) => {
              const isActive = active === l.id;
              return (
                <button
                  key={l.id}
                  onClick={() => handleNav(l.id)}
                  data-testid={`nav-link-${l.id}`}
                  aria-current={isActive ? "true" : undefined}
                  className={`relative px-4 py-2 text-sm transition-colors duration-200 ${
                    isActive ? "text-ink" : "text-ink/70 hover:text-ink"
                  }`}
                >
                  {l.label}
                  <span
                    className={`absolute left-4 right-4 -bottom-0.5 h-px transition-colors ${
                      isActive ? "bg-accent-cyan/80" : "bg-ink/0 hover:bg-ink/40"
                    }`}
                  />
                </button>
              );
            })}
            <PlaygroundButton className="ml-2" />
            <button
              onClick={() => handleNav("contact")}
              className="ml-2 group relative inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-blue-500 to-violet-500 px-4 py-2 text-sm font-semibold text-white shadow-[0_8px_30px_rgba(139,92,246,0.35)] hover:from-blue-400 hover:to-violet-400 hover:shadow-[0_12px_40px_rgba(139,92,246,0.55)] transition-[box-shadow,background-color] duration-300"
              data-testid="nav-cta"
            >
              Let&apos;s Talk
              <ArrowUpRight
                size={15}
                className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
              />
            </button>
          </div>

          <button
            className="md:hidden inline-flex h-11 w-11 items-center justify-center rounded-full border border-ink/10 bg-ink/[0.03]"
            aria-label="Open menu"
            data-testid="nav-menu-toggle"
            onClick={() => setOpen((o) => !o)}
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </nav>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-x-0 top-16 z-40 md:hidden section-x"
            data-testid="mobile-menu"
          >
            <div className="glass rounded-2xl p-3">
              <ul className="flex flex-col">
                {LINKS.map((l) => {
                  const isActive = active === l.id;
                  return (
                    <li key={l.id}>
                      <button
                        onClick={() => handleNav(l.id)}
                        data-testid={`mobile-nav-${l.id}`}
                        aria-current={isActive ? "true" : undefined}
                        className={`w-full text-left px-4 py-3 text-base rounded-xl transition-colors ${
                          isActive
                            ? "text-ink bg-ink/[0.06]"
                            : "text-ink/85 hover:text-ink hover:bg-ink/[0.04]"
                        }`}
                      >
                        {l.label}
                      </button>
                    </li>
                  );
                })}
                <li className="p-2">
                  <PlaygroundButton
                    className="w-full"
                    onClick={() => setOpen(false)}
                  />
                </li>
                <li className="px-2 pb-2">
                  <button
                    onClick={() => handleNav("contact")}
                    data-testid="mobile-nav-cta"
                    className="group w-full inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-blue-500 to-violet-500 px-4 py-3 text-sm font-semibold text-white shadow-[0_8px_30px_rgba(139,92,246,0.35)] hover:from-blue-400 hover:to-violet-400 hover:shadow-[0_12px_40px_rgba(139,92,246,0.55)] transition-[box-shadow,background-color] duration-300"
                  >
                    Let&apos;s Talk
                    <ArrowUpRight
                      size={15}
                      className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                    />
                  </button>
                </li>
              </ul>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
